import { useState } from "react";
import { useRouteLoaderData } from "react-router-dom";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useUser } from "@/providers/user";
import { useDiabolo } from "@/providers/diabolocom";
import { useRingover } from "@/providers/ringover";
import { useSms } from "@/providers/sms";
import { MessageDialog } from "@/components/tools/message/dialog";
import { toast } from "sonner";
import { isMobilePhone } from "@/utils/misc";

interface FieldActionsProps {
    email?: string | null;
    phone_number?: string | null;
    sf_object_id?: string | null;
    children?: React.ReactNode;
    className?: string;
}

const RECORD_TYPE_BY_VERBOSE_NAME: Record<string, "opportunity" | "lead" | "account" | "case"> = {
    opportunity: "opportunity",
    lead: "lead",
    account: "account",
    case: "case",
};

function normalizeRecordType(modelName: string | undefined | null): "opportunity" | "lead" | "account" | "case" | null {
    const key = (modelName ?? "").trim().toLowerCase();
    return RECORD_TYPE_BY_VERBOSE_NAME[key] ?? null;
}

export function FieldActions({ email, phone_number, sf_object_id, children, className }: FieldActionsProps) {
    const { user } = useUser();
    const { dialNumber: dialDiabolo } = useDiabolo();
    const { dialNumber: dialRingover } = useRingover();
    const { openFromPhoneNumber } = useSms();
    const routeData = useRouteLoaderData("object-detail") as
        | { object: { id: number }; metadata: { model_name: string } }
        | undefined;
    const recordId = routeData?.object?.id?.toString() ?? null;
    const recordType = normalizeRecordType(routeData?.metadata?.model_name);
    const hasVoip = user?.sf_user?.diabolocom_user_id != null || user?.sf_user?.ringover_user_id != null;
    const hasDiabolo = user?.sf_user?.diabolocom_user_id != null;
    const hasRingover = user?.sf_user?.ringover_user_id != null;
    const [isMessageOpen, setIsMessageOpen] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    const handleCopy = (item: string) => {
        navigator.clipboard.writeText(item);
        toast.success("Element copied to clipboard");
    };

    if (!phone_number && !email && !hasVoip) {
        return <>{children}</>;
    }

    return (
        <>
            <MessageDialog phone_number={phone_number || undefined} sf_object_id={sf_object_id || undefined} open={isMessageOpen} setOpen={setIsMessageOpen} />
            <div className={"relative inline-block w-full " + (className || "")} onMouseEnter={() => setIsOpen(true)} onMouseLeave={() => setIsOpen(false)}>
                <DropdownMenu open={isOpen} onOpenChange={setIsOpen} modal={false}>
                    <DropdownMenuTrigger asChild>
                        <span className="flex w-full cursor-pointer flex-nowrap items-center space-x-2 hover:text-blue-500">{children}</span>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="-mt-1 min-w-[var(--radix-dropdown-menu-trigger-width)]" sideOffset={2}>
                        {phone_number && hasDiabolo && (
                            <DropdownMenuItem
                                onMouseDown={() => {
                                    if (phone_number) dialDiabolo(phone_number, recordId, recordType);
                                }}
                                className="w-full cursor-pointer text-sm"
                            >
                                <span>Call</span>
                            </DropdownMenuItem>
                        )}
                        {phone_number && hasRingover && (
                            <DropdownMenuItem
                                onMouseDown={() => {
                                    if (phone_number) dialRingover(phone_number, recordId, recordType);
                                }}
                                className="w-full cursor-pointer text-sm"
                            >
                                <span className="whitespace-nowrap">Call (Ringover)</span>
                            </DropdownMenuItem>
                        )}
                        {phone_number && isMobilePhone(phone_number) && (
                            <DropdownMenuItem
                                onMouseDown={async e => {
                                    e.preventDefault();
                                    await openFromPhoneNumber(phone_number, { object_type: recordType, object_id: recordId });
                                }}
                                className="cursor-pointer"
                            >
                                {/* <MessageSquarePlus className="mr-2 h-4 w-4" /> */}
                                <span className="whitespace-nowrap">Send an SMS</span>
                            </DropdownMenuItem>
                        )}
                        {phone_number && (
                            <DropdownMenuItem onMouseDown={() => handleCopy(phone_number)} className="cursor-pointer text-sm">
                                <span className="whitespace-nowrap">Copy the phone number</span>
                            </DropdownMenuItem>
                        )}
                        {email && (
                            <DropdownMenuItem onMouseDown={() => (window.location.href = `mailto:${email}`)} className="w-full cursor-pointer text-sm">
                                <span className="whitespace-nowrap">Send an email</span>
                            </DropdownMenuItem>
                        )}
                        {email && (
                            <DropdownMenuItem onMouseDown={() => handleCopy(email)} className="cursor-pointer text-sm">
                                <span className="whitespace-nowrap">Copy the email</span>
                            </DropdownMenuItem>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </>
    );
}
